import React, { useState } from "react";
import {
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDocs,
  query as fsQuery,
  where,
  Timestamp,
} from "firebase/firestore";
import { ref, getBlob, deleteObject } from "firebase/storage";
import { PaperClipIcon, XMarkIcon, EyeIcon } from "@heroicons/react/20/solid";
import { db, storage } from "../../firebase";
import DocumentUploader from "./DocumentUploader";
import ArchivoPreviewModal from "./ArchivoPreviewModal";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import DialogoConfirmacion from "./DialogoConfirmacion";

const ACCENT = "#1b4d2e";
const CALENDAR_ID = "default";

const ACTIONS = {
  payment: {
    attach: BITACORA_ACTIONS.PAYMENT_COMPROBANTE_ATTACH,
    delete: BITACORA_ACTIONS.PAYMENT_COMPROBANTE_DELETE,
    entity: "payment",
    label: "Comprobante de pago",
  },
  refund: {
    attach: BITACORA_ACTIONS.REFUND_COMPROBANTE_ATTACH,
    delete: BITACORA_ACTIONS.REFUND_COMPROBANTE_DELETE,
    entity: "refund",
    label: "Comprobante de reembolso",
  },
  deposito: {
    attach: BITACORA_ACTIONS.DEPOSITO_COMPROBANTE_ATTACH,
    delete: BITACORA_ACTIONS.DEPOSITO_COMPROBANTE_DELETE,
    entity: "deposito",
    label: "Comprobante de depósito",
  },
  // Lo que sale: el comprobante de pagarle a un proveedor. Un mismo archivo
  // puede colgar de VARIOS costos —le pagas al DJ dos conceptos con una sola
  // transferencia— y por eso el borrado de abajo cuenta referencias.
  costo: {
    attach: BITACORA_ACTIONS.COSTO_COMPROBANTE_ATTACH,
    delete: BITACORA_ACTIONS.COSTO_COMPROBANTE_DELETE,
    entity: "costo",
    label: "Comprobante de egreso",
  },
};

export default function ComprobanteCell({
  venueId,
  eventId,
  eventNombre,
  linkedKind,
  linkedId,
  linkedRole,
  linkedLabel,
  archivo,
  currentUser,
  userInfo,
  onChanged,
  // Solo lectura (inversionista, P-FRA-13): solo se indica que hay
  // comprobante; no se abre, previsualiza, descarga, adjunta ni quita.
  soloLectura = false,
}) {
  const [downloading, setDownloading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const cfg = ACTIONS[linkedKind];
  const storagePrefix = venueId && eventId
    ? `venues/${venueId}/eventos/${eventId}/archivos`
    : null;

  const handleUploaded = async (payload) => {
    const archivoRef = doc(
      collection(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos")
    );
    const label = linkedLabel || cfg.label;
    const data = {
      label,
      tipo: "comprobante",
      storagePath: payload.storagePath,
      filename: payload.filename,
      fileSize: payload.fileSize,
      mimeType: payload.mimeType,
      uploadedAt: Timestamp.now(),
      uploadedBy: currentUser?.uid || null,
      uploadedByName: userInfo?.name || "",
      linkedTo: { kind: linkedKind, id: linkedId },
      ...(linkedRole ? { linkedRole } : {}),
    };
    try {
      await setDoc(archivoRef, data);
      logBitacora(venueId, {
        action: cfg.attach,
        entity: cfg.entity,
        entityId: linkedId,
        entityName: eventNombre || "",
        details: { label, filename: payload.filename, fileSize: payload.fileSize },
      }, userInfo, currentUser);
      onChanged?.();
    } catch (err) {
      console.error("Error saving comprobante doc:", err);
      showAlert("Se subió el archivo pero falló al guardar el comprobante.", "error");
      try { await deleteObject(ref(storage, payload.storagePath)); } catch (_) {}
    }
  };

  const handleDownload = async () => {
    if (!archivo?.storagePath) {
      showAlert("Este comprobante no tiene storagePath y no se puede descargar.", "error");
      return;
    }
    setDownloading(true);
    try {
      const blob = await getBlob(ref(storage, archivo.storagePath));
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = archivo.filename || archivo.label || "comprobante";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      console.error("Error downloading comprobante:", err);
      showAlert("No se pudo descargar el comprobante.", "error");
    } finally {
      setDownloading(false);
    }
  };

  const handlePreview = () => {
    if (!archivo?.storagePath) {
      showAlert("Este comprobante no tiene storagePath y no se puede abrir.", "error");
      return;
    }
    setPreviewOpen(true);
  };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const handleRemove = (e) => {
    e.stopPropagation();
    if (!archivo) return;
    setConfirmOpen(true);
  };
  const doRemove = async () => {
    setConfirmOpen(false);
    if (!archivo) return;
    try {
      await deleteDoc(doc(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos", archivo.id));
      // El objeto de Storage solo se borra si ya nadie más lo apunta. Una
      // transferencia que pagó tres costos deja tres comprobantes con el
      // mismo archivo: quitar uno no puede dejar a los otros rotos.
      if (archivo.storagePath) {
        let quedanOtros = false;
        try {
          const otros = await getDocs(
            fsQuery(
              collection(db, "venues", venueId, "calendars", CALENDAR_ID, "events", eventId, "archivos"),
              where("storagePath", "==", archivo.storagePath)
            )
          );
          quedanOtros = otros.size > 0;
        } catch (err) {
          // Si no se pudo comprobar, se CONSERVA el archivo: un huérfano en
          // Storage es barato; un comprobante roto, no.
          console.error("Error checking comprobante references:", err);
          quedanOtros = true;
        }
        if (!quedanOtros) {
          try {
            await deleteObject(ref(storage, archivo.storagePath));
          } catch (err) {
            if (err?.code !== "storage/object-not-found") {
              console.error("Error deleting storage object:", err);
            }
          }
        }
      }
      logBitacora(venueId, {
        action: cfg.delete,
        entity: cfg.entity,
        entityId: linkedId,
        entityName: eventNombre || "",
        details: { label: archivo.label, filename: archivo.filename },
      }, userInfo, currentUser);
      onChanged?.();
    } catch (err) {
      console.error("Error removing comprobante:", err);
      showAlert("No se pudo quitar el comprobante.", "error");
    }
  };

  if (!archivo) {
    if (soloLectura) return <span className="text-zinc-300">—</span>;
    return (
      <DocumentUploader
        storagePathPrefix={storagePrefix}
        iconOnly
        iconTooltip="Adjuntar comprobante"
        withDownloadUrl={false}
        disabled={!venueId || !eventId || !linkedId}
        onUploaded={handleUploaded}
      />
    );
  }

  if (soloLectura) {
    return (
      <span
        className="inline-flex h-7 w-7 items-center justify-center text-zinc-400"
        title={`Comprobante adjunto: ${archivo.filename || archivo.label || "archivo"}`}
        aria-label="Comprobante adjunto"
      >
        <PaperClipIcon className="h-4 w-4" />
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-0.5 group">
      <ArchivoPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        storagePath={archivo.storagePath}
        filename={archivo.filename}
        mimeType={archivo.mimeType}
      />
      {/* La celda vive dentro de filas con onClick: el diálogo no debe
          disparar la navegación de la fila. */}
      <span onClick={(e) => e.stopPropagation()}>
        <DialogoConfirmacion
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          titulo="Quitar comprobante"
          subtitulo={archivo.label || archivo.filename || ""}
          icono={PaperClipIcon}
          confirmar={{ label: "Quitar", tono: "rojo", onClick: doRemove }}
        >
          <p>
            El archivo se elimina y deja de estar disponible para descargar o
            ver. Esta acción no se puede deshacer.
          </p>
        </DialogoConfirmacion>
      </span>
      <button
        type="button"
        onClick={handlePreview}
        title="Ver comprobante"
        aria-label="Ver comprobante"
        className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-zinc-100"
        style={{ color: ACCENT }}
      >
        <EyeIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        title={downloading ? "Descargando..." : `Descargar ${archivo.filename || "comprobante"}`}
        aria-label="Descargar comprobante"
        className="inline-flex h-7 w-7 items-center justify-center rounded transition-colors hover:bg-zinc-100"
        style={{ color: ACCENT }}
      >
        <PaperClipIcon className="h-4 w-4" />
      </button>
      {!soloLectura && (
      <button
        type="button"
        onClick={handleRemove}
        title="Quitar comprobante"
        aria-label="Quitar comprobante"
        className="inline-flex h-5 w-5 items-center justify-center rounded text-zinc-300 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
      )}
    </div>
  );
}
